import { Component } from '@theme/component';

/**
 * A one-at-a-time carousel for the menu drawer's featured collections/products strip.
 * Clicking the arrows scrolls the track by exactly one card, looping at both ends.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrollable list of cards.
 *
 * @extends {Component<Refs>}
 */
class MenuCarousel extends Component {
  requiredRefs = ['track'];

  /**
   * @returns {HTMLElement[]}
   */
  get #items() {
    return Array.from(this.refs.track.children);
  }

  /**
   * @returns {number}
   */
  get #currentIndex() {
    const track = this.refs.track;
    const items = this.#items;
    let closest = 0;
    let closestDistance = Infinity;

    items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - track.scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = index;
      }
    });

    return closest;
  }

  /**
   * Advance to the next slide, looping back to the first at the end.
   */
  next() {
    const items = this.#items;
    if (items.length === 0) return;

    const current = this.#currentIndex;
    const nextIndex = current >= items.length - 1 ? 0 : current + 1;
    this.#scrollToIndex(nextIndex);
  }

  /**
   * Go back to the previous slide, looping to the last at the start.
   */
  prev() {
    const items = this.#items;
    if (items.length === 0) return;

    const current = this.#currentIndex;
    const prevIndex = current <= 0 ? items.length - 1 : current - 1;
    this.#scrollToIndex(prevIndex);
  }

  /**
   * @param {number} index
   */
  #scrollToIndex(index) {
    const item = this.#items[index];
    if (!item) return;

    this.refs.track.scrollTo({
      left: item.offsetLeft,
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('menu-carousel')) {
  customElements.define('menu-carousel', MenuCarousel);
}